import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { describeError, supabase, type Row } from '@/lib/supabase'

export type JobFile = Row<'job_files'>

/** The document an upload stands in for (28 Oct 2026); null is a plain file. */
export type DocumentSlot = 'quote' | 'invoice' | 'contract' | 'questionnaire'

const fileKeys = {
  forJob: (jobId: string) => ['job-files', jobId] as const,
}

/** Files on a project, newest first. A couple's upload still on its way is left out. */
export function useJobFiles(jobId: string) {
  return useQuery({
    queryKey: fileKeys.forJob(jobId),
    queryFn: async (): Promise<JobFile[]> => {
      const { data, error } = await supabase
        .from('job_files')
        .select('*')
        .eq('job_id', jobId)
        .not('uploaded_at', 'is', null)
        .order('uploaded_at', { ascending: false })
      if (error) throw error
      return data
    },
  })
}

/** Up to the documents bucket's limit. */
export const MAX_FILE_BYTES = 20 * 1024 * 1024

/**
 * Attaches a file Jena adds (a timeline, an inspiration board) to the project,
 * or, with a slot, a quote, invoice, contract or questionnaire made elsewhere.
 */
export function useAddJobFile(jobId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, shared, slot = null }: { file: File; shared: boolean; slot?: DocumentSlot | null }) => {
      if (file.size > MAX_FILE_BYTES) throw new Error('Files can be up to 20 MB each.')
      const id = crypto.randomUUID()
      const extension = file.name.includes('.') ? (file.name.split('.').pop() ?? '').toLowerCase().replace(/[^a-z0-9]/g, '') : ''
      const file_path = `files/${jobId}/${id}${extension ? `.${extension}` : ''}`
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(file_path, file, { contentType: file.type || 'application/octet-stream' })
      if (uploadError) throw uploadError
      const { error } = await supabase.from('job_files').insert({
        id,
        job_id: jobId,
        name: file.name.slice(-150),
        file_path,
        content_type: file.type || null,
        size_bytes: file.size,
        added_by: 'studio',
        shared,
        slot,
        uploaded_at: new Date().toISOString(),
      })
      if (error) {
        await supabase.storage.from('documents').remove([file_path])
        throw error
      }
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: fileKeys.forJob(jobId) }),
    onError: (error) => toast.error('Could not add the file', { description: describeError(error) }),
  })
}

export function useSetFileShared(jobId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, shared }: { id: string; shared: boolean }) => {
      const { error } = await supabase.from('job_files').update({ shared }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: fileKeys.forJob(jobId) }),
    onError: (error) => toast.error('Could not change that', { description: describeError(error) }),
  })
}

export function useRemoveJobFile(jobId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (file: JobFile) => {
      const { error } = await supabase.from('job_files').delete().eq('id', file.id)
      if (error) throw error
      await supabase.storage.from('documents').remove([file.file_path])
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: fileKeys.forJob(jobId) }),
    onError: (error) => toast.error('Could not remove the file', { description: describeError(error) }),
  })
}

/** A five-minute link to a project file. */
export async function jobFileUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from('documents').createSignedUrl(path, 300)
  if (error || !data) throw error ?? new Error('The file could not be opened.')
  return data.signedUrl
}
